/**
 * 中文首字母
 * ---------------------------------------------------------------------------
 * 让下拉框能用拼音首字母搜：输入 `az` 命中"阿哲"，`ls` 命中"林森"。
 * 前台在收银时用五笔或拼音输入法，打两个字母比翻列表快得多。
 *
 * ## 为什么不引入拼音库
 *
 * 常见的拼音库（pinyin / pinyin-pro）体积在 200KB~1MB，里面绝大部分是多音字
 * 和声调数据。我们只要首字母，用不上那些，为此让门店多下载 1MB 不划算。
 *
 * ## 原理
 *
 * `Intl.Collator('zh-CN')` 的排序规则就是拼音顺序。所以只要准备每个字母的
 * "第一个汉字"作为分界（a→阿、b→八、c→嚓…），拿目标字和这些分界比大小，
 * 落在哪一段就是哪个首字母。不需要逐字查表。
 *
 * ## 已知边界
 *
 * - 只覆盖常用简体字。生僻字、多音字按排序落到相邻字母，可能不准——
 *   但这只是**搜索辅助**，匹配不到时用户还能直接输入汉字，不会卡住流程。
 * - 姓氏多音字（如"单""解""覃"）会按最常见读音归类，这是已知取舍。
 * - 非汉字（数字、字母、符号）原样保留，所以 `M2026` 这类会员号也能搜。
 */

/**
 * 每个声母段的第一个汉字。
 * 没有 i / u / v —— 普通话里没有以这三个字母开头的音节。
 */
const BOUNDARIES: [string, string][] = [
  ['a', '阿'],
  ['b', '芭'],
  ['c', '擦'],
  ['d', '搭'],
  ['e', '蛾'],
  ['f', '发'],
  ['g', '噶'],
  ['h', '哈'],
  ['j', '击'],
  ['k', '喀'],
  ['l', '垃'],
  ['m', '妈'],
  ['n', '拿'],
  ['o', '哦'],
  ['p', '啪'],
  ['q', '期'],
  ['r', '然'],
  ['s', '撒'],
  ['t', '塌'],
  ['w', '挖'],
  ['x', '昔'],
  ['y', '压'],
  ['z', '匝'],
];

/** zh-CN 排序器。建一次就够，每次调用都 new 会明显变慢 */
const collator = new Intl.Collator('zh-CN');

/** 是否是常用汉字区间的字符 */
function isHan(char: string): boolean {
  const code = char.codePointAt(0) ?? 0;
  return code >= 0x4e00 && code <= 0x9fa5;
}

/** 单个汉字 → 声母。落不进任何段时返回空串 */
function initialOf(char: string): string {
  if (!isHan(char)) return '';
  let result = '';
  for (const [letter, boundary] of BOUNDARIES) {
    // 排在这个分界之后（含），就先记下来，继续找更靠后的段
    if (collator.compare(char, boundary) >= 0) result = letter;
    else break;
  }
  return result;
}

/**
 * 取一段文字的首字母串。
 * 汉字取声母，其余字符转小写原样保留——这样 `M20260730` 这类会员号也能搜。
 *
 * pinyinInitials('阿哲')      → 'az'
 * pinyinInitials('TONI 老板') → 'toni lb'
 */
export function pinyinInitials(text: string): string {
  let result = '';
  for (const char of text) {
    result += isHan(char) ? initialOf(char) : char.toLowerCase();
  }
  return result;
}

/**
 * 选项是否命中关键字。
 *
 * 三种命中方式，任一即可：
 *   1. 直接包含（输入汉字或数字时走这条）
 *   2. 首字母串包含（输入 `az` 命中"阿哲"）
 *   3. 首字母串按词首匹配（输入 `lb` 命中"TONI 老板"，跳过中间的空格）
 *
 * 关键字里的空格会被忽略：前台连着打 `tonilb` 也应该能命中"TONI 老板"。
 */
export function matchesKeyword(label: string, keyword: string): boolean {
  const text = keyword.trim().toLowerCase();
  if (!text) return true;
  if (label.toLowerCase().includes(text)) return true;

  const initials = pinyinInitials(label);
  if (initials.includes(text)) return true;

  // 去掉空格再比一次，允许"tonilb"这种连着打的写法
  return initials.replace(/\s+/g, '').includes(text.replace(/\s+/g, ''));
}
